import { AuthStatus, fullPhoneNumber } from '@nextforce/api'
import { useQueryClient } from '@tanstack/react-query'
import { AnalyticsConstants } from 'app/analytics/constants'
import { useAnalytics } from 'app/analytics/context'
import { useServer } from 'app/server/context'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Platform } from 'react-native'
import { useThemeChange } from 'settings/change_theme'
import { useIosSetting } from 'shared/hooks/ios_settings'
import { clearStorage } from 'shared/storage'
import FreshChat from 'shared/wrappers/react-native-freshchat-sdk'
import { useResetBadgeCount } from 'users/queries/reset_badge'
import {
  SIMULATOR_ERROR,
  deregisterDevice,
  registerDevice,
} from '../notifications/push'
import { ServerAuthenticationService } from './service'
import { AuthenticationService } from './types'
export interface AuthenticationContextValue {
  status: AuthStatus
  login?(phoneNumber: string, code: string): Promise<void>
  logout?(userId: string): Promise<void>
  profileNeedsReview: boolean | undefined
  setProfileNeedsReview: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >
}

export const AuthenticationContext = createContext({
  status: 'idle',
  profileNeedsReview: undefined,
} as AuthenticationContextValue)

interface Props {
  children: React.ReactNode
}

export function AuthenticationProvider(props: Props) {
  const { server, onReset } = useServer()
  const analytics = useAnalytics()
  const resetBadgeCountMutation = useResetBadgeCount()
  const client = useQueryClient()

  const [authentication, setAuthentication] = useState<
    AuthenticationService | undefined
  >()

  const [status, setStatus] = useState<AuthStatus>('loading')
  const [deviceToken, setDeviceToken] = useState<string | undefined>()

  const [profileNeedsReview, setProfileNeedsReview] = useState<
    boolean | undefined
  >()

  const [resetCacheOnNextLaunch, setResetCacheOnNextLaunch] =
    useIosSetting<boolean>('reset_cache_on_next_launch')
  const resetCacheRef = useRef(resetCacheOnNextLaunch)

  const clearCache = useCallback(async () => {
    client.removeQueries()
    client.clear()
    return clearStorage()
  }, [client])

  const login = useCallback(
    async (phoneNumber: string, code: string, reviewProfile?: boolean) => {
      setProfileNeedsReview(reviewProfile)
      return authentication?.login(fullPhoneNumber(phoneNumber), code)
    },
    [authentication]
  )

  const { setThemeToSystem } = useThemeChange()

  const logout = useCallback(
    async (userId: string) => {
      if (deviceToken) {
        deregisterDevice(server, deviceToken).catch(err =>
          console.warn('Error deregistering device:', err)
        )
      }
      if (Platform.OS !== 'web') {
        FreshChat.Freshchat.resetUser()
        await resetBadgeCountMutation.mutateAsync()
      }
      analytics
        .track(userId, AnalyticsConstants.ANALYTICS_EVENT_LOGOUT)
        .catch(err => console.error('Error tracking logout ', err))
      if (authentication) {
        await authentication.logout()
      }
      await clearCache()
      setThemeToSystem()
    },
    [
      analytics,
      authentication,
      clearCache,
      deviceToken,
      resetBadgeCountMutation,
      server,
      setThemeToSystem,
    ]
  )

  useEffect(() => {
    if (server) {
      setAuthentication(new ServerAuthenticationService(server))
    }
  }, [server])

  useEffect(() => {
    if (authentication) {
      authentication.onStatusChanged(updatedAuthState => {
        setStatus(updatedAuthState)
      })
      return () => {
        authentication?.onStatusChanged(undefined)
      }
    }
  }, [authentication])

  useEffect(() => {
    if (status === 'loggedIn') {
      if (!deviceToken) {
        registerDevice(server, client)
          .then(setDeviceToken)
          .catch((err: Error) => {
            if (err.name !== SIMULATOR_ERROR) {
              console.warn('Error getting device token: ', err.message)
            }
          })
      }
    }
  }, [status, deviceToken, server, client])

  useEffect(() => {
    if (authentication) {
      if (resetCacheRef.current) {
        authentication
          .logout()
          .then(() => onReset())
          .then(() => clearCache())
          .then(() => {
            setResetCacheOnNextLaunch(false)
            resetCacheRef.current = false
          })
          .catch(e => null)
      }
    }
  }, [authentication, clearCache, onReset, setResetCacheOnNextLaunch])

  return (
    <AuthenticationContext.Provider
      value={
        authentication
          ? {
              login,
              logout,
              status,
              profileNeedsReview,
              setProfileNeedsReview,
            }
          : {
              status,
              profileNeedsReview,
              setProfileNeedsReview,
            }
      }
    >
      {props.children}
    </AuthenticationContext.Provider>
  )
}

export function useAuthentication() {
  return useContext<AuthenticationContextValue>(AuthenticationContext)
}
